import { computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CampaignAutomationStatusEnum, TypeCampaignEnum } from '@/types';
import { findPermission, UserState } from '@/composables/User';
import { store } from '@/store';
import { getNotConfiguredServices } from '@/composables/shop/MyShopParameters';
import { fetchFlowStructure } from '@/composables/flows/useFlowApi';
import type { RowActionOption, GroupedActionOption, SelectionInfo } from '@/components/table-rest-api/types';
import { isWorkshopAccount } from '@/composables/WorkshopMode';
import type { FlowStructureDto, FlowStructureSettingsRaw } from '@/types/flow-structure';
import type { FlowSettingsAutomation } from '@/types/flow-settings';
import { getDefaultAutomationSettings } from '@/types/flow-settings';
import type { AutomationCampaignData, CampaignActionPayload } from './types';

/**
 * Interface for action component refs
 */
interface ActionComponentRef {
  open?: (payload: CampaignActionPayload) => void;
}

// Operators to exclude from service configuration check for automation campaigns
const excludedOperators = ['boxsendfbmessenger'];

/**
 * Extract operators from flow structure steps
 */
const getOperatorsFromFlowStructure = (flowData: FlowStructureDto): string[] => {
  if (!flowData.steps || !Array.isArray(flowData.steps)) {
    return [];
  }
  return flowData.steps
    .map((step) => step.parameters?.id)
    .filter((id): id is string => !!id && !excludedOperators.includes(id));
};

/**
 * Transform raw API settings to internal FlowSettingsAutomation format
 * Converts trigger_time/unit_time to time_unit_select
 */
const transformApiSettingsToInternal = (
  rawSettings: FlowStructureSettingsRaw | undefined,
  campaignName: string,
): FlowSettingsAutomation => {
  const defaults = getDefaultAutomationSettings(campaignName);

  if (!rawSettings) {
    return defaults;
  }

  // The persisted JSON stores binaries as strings (legacy PHP origin) — coerce
  // explicitly so the editor's strict `=== 1` comparisons match.
  const toBinary = (v: unknown, fallback: 0 | 1): 0 | 1 => {
    if (v == null) return fallback;
    return Number(v) === 1 ? 1 : 0;
  };
  const toBinaryNullable = (
    v: unknown,
    fallback: 0 | 1 | null,
  ): 0 | 1 | null => {
    if (v == null) return fallback;
    return Number(v) === 1 ? 1 : 0;
  };

  // ⚠️ Persisted key is `type_retriggered` (with the extra "ed"), while the
  // internal type uses `type_retrigger`. Read both defensively so the fix
  // survives whether the backend renames the field or passes it through raw.
  const rawRetrigger = (rawSettings as { type_retriggered?: unknown }).type_retriggered
    ?? rawSettings.type_retrigger;

  return {
    name: campaignName,
    legend: rawSettings.legend ?? defaults.legend,
    type_workflow: rawSettings.type_workflow ?? defaults.type_workflow,
    unique: toBinary(rawSettings.unique, defaults.unique),
    type_unique: toBinaryNullable(rawSettings.type_unique, defaults.type_unique),
    type_retrigger: toBinaryNullable(rawRetrigger, defaults.type_retrigger),
    time_unit_select: {
      unit: rawSettings.unit_time ?? defaults.time_unit_select.unit,
      value: rawSettings.trigger_time != null
        ? Number(rawSettings.trigger_time) || defaults.time_unit_select.value
        : defaults.time_unit_select.value,
    },
    program_date_ranges: rawSettings.program_date_ranges ?? defaults.program_date_ranges,
    date_ranges: rawSettings.date_ranges ?? defaults.date_ranges,
    schedule_config: rawSettings.schedule_config ?? defaults.schedule_config,
    track_sales: rawSettings.track_sales ?? defaults.track_sales,
  };
};

/**
 * Open automation campaign in flow editor with all necessary checks
 * This is exported separately to be reused in campaign creation flows
 */
export const openAutomationCampaignEditor = async (
  campaignId: number,
  campaignName: string,
  onClose?: () => void,
): Promise<void> => {
  const idShop = UserState.activeShop?.id ?? 0;

  store.commit('general/showTheSpinner');

  try {
    // Fetch flow structure first
    const flowData: FlowStructureDto = await fetchFlowStructure(TypeCampaignEnum.AUTOMATION, campaignId);
    console.log('flowData', flowData);
    // Transform API settings to internal format
    if (flowData.settings && flowData.settings.name && campaignName === '') {
      // eslint-disable-next-line no-param-reassign
      campaignName = flowData.settings.name;
    }
    const settings = transformApiSettingsToInternal(flowData.settings, campaignName);

    // Extract operators from flow structure steps
    const operators = getOperatorsFromFlowStructure(flowData);

    // Check for services that need configuration
    const notConfiguredServices = await getNotConfiguredServices(idShop, ['editScenarioCampaign', ...operators]);

    const openFlowEditor = () => {
      store.dispatch('flowEditor/openFlow', {
        id: campaignId,
        flowData,
        type: TypeCampaignEnum.AUTOMATION,
        name: campaignName,
        settings,
      });
      if (onClose) onClose();
      store.commit('general/hideTheSpinner');
    };

    if (notConfiguredServices.length) {
      store.commit('general/setNotConfiguredServices', notConfiguredServices);
      store.commit('general/setIsServicesParametersModalVisible', true);
      store.commit('general/setServiceParameterSuccessAction', openFlowEditor);
      store.commit('general/hideTheSpinner');
    } else {
      openFlowEditor();
    }
  } catch (error) {
    console.error('Error opening flow editor:', error);
    store.commit('general/hideTheSpinner');
  }
};

/**
 * Options for useAutomationCampaignsActions composable
 */
interface UseAutomationCampaignsActionsOptions {
  activateRef: Ref<ActionComponentRef | null>;
  deactivateRef: Ref<ActionComponentRef | null>;
  duplicateRef: Ref<ActionComponentRef | null>;
  deleteRef: Ref<ActionComponentRef | null>;
  onPublishToGallery?: (selection: SelectionInfo) => void;
  onToggleTrackSales?: (
    campaign: AutomationCampaignData,
    trackSales: boolean,
  ) => void;
}

export default function useAutomationCampaignsActions(options: UseAutomationCampaignsActionsOptions) {
  const { t } = useI18n();

  /**
   * Handle edit action - opens the campaign editor (new flow editor)
   */
  const handleEdit = async (campaign: AutomationCampaignData) => {
    if (!campaign?.id) return;

    // Use shared function to open the editor
    await openAutomationCampaignEditor(campaign.id, campaign.name);
  };

  const rowActions = computed<RowActionOption[]>(() => [
    // Deactivate action - visible only when status is ACTIVE
    {
      label: t('campaignsAutomationList.actions.deactivate'),
      icon: 'far fa-times',
      class: 'p-button-danger',
      visible: (data: AutomationCampaignData) => findPermission('automated_scenarios.activate')
        && data.status === CampaignAutomationStatusEnum.ACTIVE,
      command: (data: AutomationCampaignData) => {
        if (options.deactivateRef.value?.open) {
          options.deactivateRef.value.open({ campaign: data });
        }
      },
    },
    // Activate action - visible only when status is DISABLED
    {
      label: t('campaignsAutomationList.actions.activate'),
      icon: 'far fa-check',
      visible: (data: AutomationCampaignData) => findPermission('automated_scenarios.activate')
        && data.status === CampaignAutomationStatusEnum.DISABLED,
      command: (data: AutomationCampaignData) => {
        if (options.activateRef.value?.open) {
          options.activateRef.value.open({ campaign: data });
        }
      },
    },
    // Track sales action - visible only when active and sales are NOT currently tracked
    {
      label: t('campaignsAutomationList.actions.trackSales'),
      icon: 'far fa-cart-shopping',
      visible: (data: AutomationCampaignData) => findPermission('automated_scenarios.edit')
        && data.status === CampaignAutomationStatusEnum.ACTIVE
        && data.track_sales !== true,
      command: (data: AutomationCampaignData) => {
        options.onToggleTrackSales?.(data, true);
      },
    },
    // Untrack sales action - visible only when active and sales ARE currently tracked
    {
      label: t('campaignsAutomationList.actions.untrackSales'),
      icon: 'far fa-ban',
      visible: (data: AutomationCampaignData) => findPermission('automated_scenarios.edit')
        && data.status === CampaignAutomationStatusEnum.ACTIVE
        && data.track_sales === true,
      command: (data: AutomationCampaignData) => {
        options.onToggleTrackSales?.(data, false);
      },
    },
    // Edit action
    {
      label: t('campaignsAutomationList.actions.edit'),
      icon: 'far fa-edit',
      visible: () => findPermission('automated_scenarios.edit'),
      command: (data: AutomationCampaignData) => handleEdit(data),
    },
    // Duplicate action
    {
      label: t('campaignsAutomationList.actions.duplicate'),
      icon: 'far fa-clone',
      visible: () => findPermission('automated_scenarios.duplicate'),
      command: (data: AutomationCampaignData) => {
        if (options.duplicateRef.value?.open) {
          options.duplicateRef.value.open({ campaign: data });
        }
      },
    },
    // Delete action
    {
      label: t('campaignsAutomationList.actions.delete'),
      icon: 'far fa-trash',
      visible: () => findPermission('automated_scenarios.delete'),
      command: (data: AutomationCampaignData) => {
        if (options.deleteRef.value?.open) {
          options.deleteRef.value.open({ campaign: data });
        }
      },
    },
  ]);

  const groupedActions = computed<GroupedActionOption[]>(() => {
    const actions: GroupedActionOption[] = [];

    if (isWorkshopAccount.value) {
      actions.push({
        label: t('campaignsAutomationList.groupedActions.publishToGallery'),
        icon: 'fa fa-cloud-arrow-up',
        command: (selection: SelectionInfo) => {
          if (options.onPublishToGallery) {
            options.onPublishToGallery(selection);
          }
        },
      });
    }

    return actions;
  });

  return {
    rowActions,
    groupedActions,
  };
}
